import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";
import { useDataId } from "utils/hooks";
import type { DefaultComponent } from "types/DefaultComponent";

export interface StickyHeaderContainerProps extends DefaultComponent {
	initHidden?: boolean;
	dataQAPrefix?: string;
	dataQASuffix?: string;
}

/**
 * Internal type to wrap up the StickyElements
 */
type StickyElement = {
	el: HTMLElement;
	parent: HTMLElement;
	placeholder: HTMLElement;
	position: number;
	height: number;
};

/**
 * StickyHeaderContainer
 * @param children  Child components to render
 * @param initHidden  Set visibility state
 * @param dataQAPrefix: Optional QA naming attribute prefix
 * @param dataQASuffix: Optional QA naming attribute suffix
 * @returns StickyHeaderContainer (React.ReactNode)
 */
function StickyHeaderContainer({
	children,
	initHidden = false,
	dataQA = "sticky-header-container",
	dataQAPrefix = "",
	dataQASuffix = ""
}: PropsWithChildren<StickyHeaderContainerProps>) {
	const [globalHeaderHeight, setGlobalHeaderHeight] = useState<number>(0);
	const [prevScroll, setPrevScroll] = useState<number>(0);
	const [stickies, setStickies] = useState<StickyElement[]>([]);
	const [didInit, setDidInit] = useState<boolean>(false);

	const TRANSITION_STYLE = "all 0.5s, opacity 0.2s";
	const dataQAId = useDataId(dataQA, { prefix: dataQAPrefix, suffix: dataQASuffix });

	/**
	 * Initialises the stickies collection
	 */
	const initStickies = useCallback(() => {
		let el: HTMLElement | null = null;
		let parentElement: HTMLElement | null = null;
		const tempStickies: Array<StickyElement> = [];

		setPrevScroll(0);

		Array.from(document.querySelectorAll(".sticky-header")).forEach((parent) => {
			el = parent.querySelectorAll(".sticky-header-el")[0] as HTMLElement;
			parentElement = parent as HTMLElement;

			tempStickies.push({
				el,
				parent: parentElement,
				placeholder: parentElement.querySelectorAll(".sticky-header-placeholder")[0] as HTMLElement,
				position: parentElement.offsetTop,
				height: el.getElementsByClassName("global-header")[0] ? globalHeaderHeight : el.offsetHeight
			});
		});

		if (initHidden) {
			tempStickies[1].el.style.opacity = "0";
		}

		setStickies(tempStickies);
	}, [globalHeaderHeight, initHidden]);

	/**
	 * Handle scroll and reposition
	 */
	const handleScroll = useCallback(() => {
		const scrollTop = Math.max(
			window.scrollY,
			document.documentElement.scrollTop,
			document.body.scrollTop
		);
		if (stickies.length > 1) {
			let next;
			let prev;
			const topHeader = stickies[0];
			topHeader.height = topHeader.el.querySelector(".show-mobile-menu#global-header")
				? globalHeaderHeight + topHeader.el.offsetHeight
				: topHeader.el.offsetHeight;
			topHeader.placeholder.style.height = topHeader.height.toString();
			topHeader.el.classList.add("first-sticky");
			const topHeaderHeight = topHeader.placeholder.style.height;
			stickies.forEach((curr, i) => {
				if (curr.position <= scrollTop) {
					next = stickies[i + 1];
					prev = stickies[i - 1];
					curr.parent.classList.add("fixed");
					if (curr.el.style.opacity === "0") {
						curr.el.style.opacity = "1";
					}
					curr.placeholder.style.height = `${curr.height}px`;
					if (next) {
						const height = curr.el.querySelector(".show-mobile-menu#global-header")
							? 0
							: curr.height;
						curr.el.style.top =
							Math.min(Math.max(0, scrollTop - next.position + height), height) * -1 + "px";
					}
					if (curr.el.style.top === topHeaderHeight) {
						curr.el.style.top = "";
					}
					topHeader.el.style.transition = "";
					if (prevScroll >= scrollTop) {
						topHeader.parent.classList.add("fixed");
						topHeader.el.style.top = "0px";
						topHeader.el.style.transition = TRANSITION_STYLE;
						if (i > 0) {
							if (curr.el.style.top === "" || curr.el.style.top === "0px") {
								curr.el.style.top = topHeaderHeight;
								curr.el.style.transition = TRANSITION_STYLE;
							}
						}
					} else {
						if (prev) {
							topHeader.el.style.transition = TRANSITION_STYLE;
						}
					}
				} else {
					prev = stickies[i - 1];
					curr.parent.classList.remove("fixed");
					curr.placeholder.style.height = "";
					curr.el.style.top = "0px";
					if (curr.el.style.opacity === "1") {
						curr.el.style.opacity = "0";
					}
					if (prev) {
						if (scrollTop <= prev.position - prev.height) {
							prev.el.style.top = "";
						}
					}
				}
			});
			setPrevScroll(scrollTop);
		} else if (stickies.length === 1) {
			let sticky = stickies[0];
			// for public site do not change sticky height when the mobile menu height goes to 0
			sticky.height = sticky.el.querySelector(".show-mobile-menu#global-header")
				? globalHeaderHeight + sticky.el.offsetHeight
				: sticky.el.offsetHeight;
			sticky.placeholder.style.height = sticky.height + "px";
			sticky.parent.classList.add("fixed");
		}
	}, [globalHeaderHeight, prevScroll, stickies]);

	const refreshView = useCallback(() => {
		initStickies();
		handleScroll();
	}, [initStickies, handleScroll]);

	/** ResizeObserver */
	const globalHeaderRef = useRef<HTMLDivElement>(
		document.getElementById("global-header") as HTMLDivElement
	);
	const { height } = useResizeObserver({
		ref: globalHeaderRef,
		box: "border-box",
		onResize: initStickies
	});

	useEffect(() => {
		if (stickies.length > 0 && !didInit) {
			handleScroll();
			setDidInit(true);
		}
	}, [stickies, didInit, handleScroll]);

	useEffect(() => {
		if (
			globalHeaderRef.current &&
			!globalHeaderRef.current.classList.contains("show-mobile-menu")
		) {
			setGlobalHeaderHeight(height!);
		}

		if (!didInit) {
			initStickies();
		}
		window.addEventListener("load", refreshView);
		window.addEventListener("scroll", handleScroll);
		window.addEventListener("resize", refreshView);

		return () => {
			window.removeEventListener("load", refreshView);
			window.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", refreshView);
		};
	}, [handleScroll, height, refreshView, didInit, initStickies]);

	return (
		<div className="sticky-header-container" data-qa={dataQAId}>
			{children}
		</div>
	);
}

export default StickyHeaderContainer;
